import { useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Group,
  Modal,
  Paper,
  ScrollArea,
  SegmentedControl,
  Stack,
  Table,
  Text,
  Textarea,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconCheck, IconX } from '@tabler/icons-react';
import { createFileRoute } from '@tanstack/react-router';
import { AccessRequest } from '~api';
import { api } from '~portal/api';
import { useAdminAccess } from '~portal/hooks';

export const Route = createFileRoute('/_protected/admin/users')({
  component: AdminRequestsPage,
});

const statusColours: Record<AccessRequest.Status, string> = {
  pending: 'yellow',
  approved: 'green',
  denied: 'red',
};

/**
 * @desc Product names matching the Subscribers page's onboarding columns, so a request and the
 * estate state it leads to read as one thing.
 */
const productLabels: Partial<Record<AccessRequest.Product, string>> = {
  [AccessRequest.products.Agent]: 'Agent Beta',
};

/** @desc Intake channel, which decides what approval does (design D7). */
const sourceLabels: Record<AccessRequest.Source, { label: string; colour: string }> = {
  legacy: { label: 'Legacy', colour: 'grape' },
  public: { label: 'Public', colour: 'blue' },
};

type PendingDecision =
  | { type: 'approve'; request: AccessRequest.Serial }
  | { type: 'deny'; request: AccessRequest.Serial };

/**
 * @desc What the decision actually does, which differs by channel (design D7/D8): a `public`
 * request is invited on approval, a `legacy` one was provisioned at signup and is upgraded from
 * `agent-pending` to `agent-beta` instead. Denying a legacy request deactivates the account.
 */
const decisionCopy = ({ type, request }: PendingDecision): string => {
  const isLegacy = AccessRequest.sourceOf(request) === AccessRequest.sources.Legacy;

  if (type === 'approve') {
    return isLegacy
      ? 'Approving grants agent access and emails the confirmation to '
      : 'Approving sends a WorkOS invitation email to ';
  }

  return isLegacy
    ? 'Denying deactivates the provisioned account of '
    : 'Deny the access request from ';
};

function AdminRequestsPage() {
  // Onboarding and denying are admin writes: an admin viewer reads the queue only (D21).
  const { write } = useAdminAccess();
  const [statusFilter, setStatusFilter] = useState<AccessRequest.Status>(
    AccessRequest.statuses.Pending,
  );
  const [decision, setDecision] = useState<PendingDecision | null>(null);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [confirmOpened, { open: openConfirm, close: closeConfirm }] = useDisclosure(false);

  const requestsQuery = api.accessRequests.list.useQuery({ status: statusFilter });

  // A request records the WorkOS organisation id it provisioned; the name comes from the
  // organisations listing rather than being denormalised onto the request.
  const organisationsQuery = api.organisations.list.useQuery();
  const organisationNames = new Map(
    (organisationsQuery.data ?? []).map((org) => [org.id, org.name]),
  );

  const dismiss = () => {
    closeConfirm();
    setDecision(null);
    setNote('');
  };

  const onSettled = (err?: { message: string }) => {
    dismiss();
    setError(err?.message ?? null);
    if (!err) requestsQuery.refetch();
  };

  const approveMutation = api.accessRequests.approve.useMutation({
    onSuccess: () => onSettled(),
    onError: (err) => onSettled(err),
  });

  const denyMutation = api.accessRequests.deny.useMutation({
    onSuccess: () => onSettled(),
    onError: (err) => onSettled(err),
  });

  const deciding = approveMutation.isPending || denyMutation.isPending;

  const confirmDecision = () => {
    if (!decision) return;

    setError(null);

    if (decision.type === 'approve') {
      approveMutation.mutate({ requestId: decision.request._id });
      return;
    }

    const trimmed = note.trim();
    denyMutation.mutate({
      requestId: decision.request._id,
      ...(trimmed ? { note: trimmed } : {}),
    });
  };

  const startDecision = (next: PendingDecision) => {
    setDecision(next);
    setNote('');
    openConfirm();
  };

  const requests = requestsQuery.data ?? [];

  return (
    <Stack gap="lg" p="lg">
      <Modal opened={confirmOpened} onClose={dismiss} title="Confirm decision" centered>
        <Stack gap="md">
          {decision && (
            <Text>
              {decisionCopy(decision)}
              <Text span fw={600}>
                {decision.request.email}
              </Text>
              .
            </Text>
          )}

          {decision?.type === 'deny' && (
            <Textarea
              label="Note"
              description="Optional — recorded against the request"
              value={note}
              maxLength={AccessRequest.MAX_NOTE_LENGTH}
              onChange={(e) => setNote(e.currentTarget.value)}
              autosize
              minRows={2}
            />
          )}

          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={dismiss}>
              Cancel
            </Button>
            <Button
              color={decision?.type === 'approve' ? 'teal' : 'red'}
              onClick={confirmDecision}
              loading={deciding}
            >
              {decision?.type === 'approve'
                ? AccessRequest.sourceOf(decision.request) === AccessRequest.sources.Legacy
                  ? 'Onboard'
                  : 'Onboard & invite'
                : 'Deny'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Title order={1}>Requests</Title>

      {error && (
        <Alert color="red" title="Error" withCloseButton onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper withBorder p="md">
        <Group justify="space-between" mb="md">
          <SegmentedControl
            value={statusFilter}
            onChange={(value) => setStatusFilter(value as AccessRequest.Status)}
            data={[
              { value: AccessRequest.statuses.Pending, label: 'Pending' },
              { value: AccessRequest.statuses.Approved, label: 'Approved' },
              { value: AccessRequest.statuses.Denied, label: 'Denied' },
            ]}
          />
          <Button
            onClick={() => requestsQuery.refetch()}
            loading={requestsQuery.isFetching}
            size="xs"
            variant="light"
          >
            Refresh
          </Button>
        </Group>

        {requests.length === 0 && !requestsQuery.isLoading ? (
          <Text c="dimmed" size="sm">
            No {statusFilter} access requests
          </Text>
        ) : (
          <ScrollArea>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Email</Table.Th>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Source</Table.Th>
                  <Table.Th>Organisation</Table.Th>
                  <Table.Th>Product</Table.Th>
                  <Table.Th>Company</Table.Th>
                  <Table.Th>Requested</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {requests.map((request) => {
                  const source = sourceLabels[AccessRequest.sourceOf(request)];

                  return (
                    <Table.Tr key={request._id}>
                      <Table.Td>
                        <Text size="sm">{request.email}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{request.name}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge color={source.colour} variant="light" size="sm">
                          {source.label}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">
                          {request.workosOrgId
                            ? (organisationNames.get(request.workosOrgId) ?? request.workosOrgId)
                            : '—'}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        {request.requestedProduct ? (
                          <Badge size="xs" variant="light" color="teal">
                            {productLabels[request.requestedProduct] ?? request.requestedProduct}
                          </Badge>
                        ) : (
                          <Text size="sm">—</Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{request.company ?? '—'}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{new Date(request.createdAt).toLocaleDateString()}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge color={statusColours[request.status]} variant="filled" size="sm">
                          {request.status}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        {request.status === AccessRequest.statuses.Pending ? (
                          <Group gap="xs" wrap="nowrap">
                            <Button
                              size="xs"
                              variant="light"
                              color="teal"
                              leftSection={<IconCheck size={14} />}
                              onClick={() => startDecision({ type: 'approve', request })}
                              disabled={!write}
                            >
                              Onboard
                            </Button>
                            <Button
                              size="xs"
                              variant="light"
                              color="red"
                              leftSection={<IconX size={14} />}
                              onClick={() => startDecision({ type: 'deny', request })}
                              disabled={!write}
                            >
                              Deny
                            </Button>
                          </Group>
                        ) : (
                          <Text size="xs" c="dimmed">
                            {request.note ?? '—'}
                          </Text>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        )}
      </Paper>

      {/* Placeholder: subscriber API access requests will surface here once that intake lands. */}
      <Paper withBorder p="md">
        <Group gap="xs" mb="xs">
          <Title order={4}>API access requests</Title>
          <Badge size="xs" variant="light" color="gray">
            coming soon
          </Badge>
        </Group>
        <Text size="sm" c="dimmed">
          Requests for subscriber API access will appear here alongside portal access requests, so
          this page stays the single queue of actionable items.
        </Text>
      </Paper>
    </Stack>
  );
}
